import { createFormHook, createFormHookContexts } from "@tanstack/react-form";
import { useQuery } from "@tanstack/react-query";
import { ClientOnly } from "@tanstack/react-router";
import clsx from "clsx";
import { Loader2, Plus, RefreshCwIcon, Trash2 } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { z } from "zod";
import { ApplicationCost, ReviewTypeArray, ReviewTypeLabelMap } from "@/constants";
import type { ReviewRequestInput } from "@/lib/client/review";
import { userPointsQueryOptions } from "@/lib/client/user";
import { reviewRequestSchema, submitReviewRequest, updateReviewRequest } from "@/lib/client/review";
import { ContactUs } from "@/features/shared/components/contact-us";
import FormErrorMessage from "@/features/shared/components/form-error-message";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";

const { fieldContext, formContext } = createFormHookContexts();

const { useAppForm } = createFormHook({
  fieldComponents: {
    Input,
    Textarea,
    Select,
    Checkbox,
  },
  formComponents: {
    Button,
  },
  fieldContext,
  formContext,
});

const reviewTypeItems = ReviewTypeArray.map((type) => ({
  value: type,
  label: ReviewTypeLabelMap[type],
}));

const reviewApplicationTerms = [
  <>
    复盘为 <strong>免费服务</strong>
    ，您的申请我们会尽力处理，但因资源与审核原因，无法保证每一份申请都一定被受理或在固定时间内完成。
  </>,
  <>
    建议使用 <strong>Outplayed / OBS</strong>{" "}
    进行录屏，确保视频画面清晰、完整，能真实还原您的对局流程与操作细节。
  </>,
  <>
    录制完成后请上传到 <strong>Bilibili</strong>。建议您：
  </>,
  <>
    历史记录页面会显示复盘申请的 <strong>队列位置</strong>，该位置会根据队列变化{" "}
    <strong>动态变动</strong>，这是正常现象。
  </>,
  <>复盘结束后，您消耗的积分会返还。</>,
];

const reviewApplicationTermDetails: Record<number, string[]> = {
  2: ["按关键阶段切分视频章节；", "或在评论区标注关键时间点，方便我们快速定位分析。"],
};

// Drafts may contain incomplete input, but must remain safe to render.
const reviewRequestDraftSchema = reviewRequestSchema
  .extend({
    description: z.string(),
    videoLinks: z.array(z.object({ url: z.string() })).min(1),
  })
  .partial();

export function ApplicationForm({
  initialValues,
  id,
  onSuccess,
}: {
  initialValues: ReviewRequestInput;
  id?: string;
  onSuccess: () => void;
}) {
  const isUpdateMode = !!id;

  const {
    data: points,
    refetch: refetchUserPoints,
    isFetching: isFetchingUserPoints,
  } = useQuery(userPointsQueryOptions);

  const insufficientPoints = useMemo(
    () => (points === undefined ? false : BigInt(points) < ApplicationCost),
    [points],
  );
  const [agreedTerms, setAgreedTerms] = useState(() =>
    new Array(reviewApplicationTerms.length).fill(false),
  );
  const allTermsAgreed = useMemo(() => agreedTerms.every(Boolean), [agreedTerms]);

  const toggleTerm = useCallback((index: number) => {
    setAgreedTerms((prev) => {
      const next = [...prev];
      next[index] = !next[index];
      return next;
    });
  }, []);

  // Keep SSR and hydration identical; apply local drafts after mounting.
  const [formInitialValues, setFormInitialValues] = useState<ReviewRequestInput>(initialValues);

  useEffect(() => {
    if (isUpdateMode) return;
    try {
      const raw = localStorage.getItem("review-request-draft");
      if (!raw) return;
      const draft = reviewRequestDraftSchema.safeParse(JSON.parse(raw));
      if (draft.success) {
        setFormInitialValues((base) => ({ ...base, ...draft.data }));
      }
    } catch {
      // 草稿损坏或不可读时忽略，保留 initialValues
    }
  }, [isUpdateMode]);

  const form = useAppForm({
    defaultValues: formInitialValues,
    validators: {
      onChange: reviewRequestSchema,
    },
    listeners: {
      onChange(props) {
        if (!isUpdateMode) {
          localStorage.setItem("review-request-draft", JSON.stringify(props.formApi.state.values));
        }
      },
    },
    async onSubmit({ value }) {
      try {
        const filteredValue = {
          ...value,
          videoLinks: value.videoLinks.filter((link) => link.url.trim() !== ""),
        };

        if (isUpdateMode) {
          const { downgrade } = await updateReviewRequest({
            data: {
              ...filteredValue,
              id,
            },
          });

          if (downgrade) {
            toast.warning("修改成功，但您在过去一个月内提交过舰长复盘请求，自动降级为普通队列");
          } else {
            toast.success("修改成功");
          }
        } else {
          const { data: latestPoints } = await refetchUserPoints();
          const insufficient = latestPoints === undefined || BigInt(latestPoints) < ApplicationCost;
          if (insufficient)
            return toast.error("积分不足，你可以通过邀请奖励获取积分，或是等待之前的复盘申请完成");

          const { downgrade } = await submitReviewRequest({
            data: filteredValue,
          });

          if (downgrade) {
            toast.warning("提交成功，但您在过去一个月内提交过舰长复盘请求，自动降级为普通队列");
          } else {
            toast.success("复盘申请提交成功");
          }
        }

        if (!isUpdateMode) localStorage.removeItem("review-request-draft");
        setFormInitialValues(initialValues);
        form.reset(initialValues);
        onSuccess();
      } catch (error) {
        toast.error(error instanceof Error ? error.message : "提交失败，请稍后再试");
      }
    },
  });

  // Function to remove a video link field
  const removeVideoLink = useCallback(
    (index: number) => {
      const currentLinks = form.getFieldValue("videoLinks");
      if (currentLinks.length > 1) {
        form.setFieldValue(
          "videoLinks",
          currentLinks.filter((_, i) => i !== index),
        );
      }
    },
    [form],
  );

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        form.handleSubmit();
      }}
      className="w-full min-w-0 overflow-x-hidden"
    >
      <Card className="min-w-0 w-full">
        <CardHeader>
          <CardTitle>申请复盘</CardTitle>
          <div className="mt-3 text-sm text-muted-foreground space-y-2">
            <p className="font-semibold text-foreground">申请复盘说明</p>
            <ol className="space-y-1.5 pl-0 text-sm">
              {reviewApplicationTerms.map((term, index) => (
                <li key={`review-term-${index}`} className="relative space-y-2">
                  <div className="flex gap-2">
                    <span className="leading-6 text-sm shrink-0 text-right text-muted-foreground">
                      {index + 1}.
                    </span>
                    <label className="flex items-start gap-2 flex-1">
                      <Checkbox
                        className="shrink-0 size-4 relative top-1"
                        checked={agreedTerms[index]}
                        onCheckedChange={() => toggleTerm(index)}
                      />
                      <span className="leading-6 text-sm">{term}</span>
                    </label>
                  </div>
                  {reviewApplicationTermDetails[index] && (
                    <ul className="list-disc pl-10 mt-1 space-y-1">
                      {reviewApplicationTermDetails[index].map((detail) => (
                        <li key={detail}>{detail}</li>
                      ))}
                    </ul>
                  )}
                </li>
              ))}
            </ol>
            {!allTermsAgreed && (
              <p className="text-xs text-orange-500 mt-2">请先勾选以上全部说明后再提交。</p>
            )}
          </div>
        </CardHeader>
        <CardContent className="flex-col space-y-4 min-w-0">
          <div className="space-y-4">
            <div className="space-y-1.5">
              <label
                className="text-sm font-semibold leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                htmlFor="video-link-0"
              >
                视频链接
              </label>
              <p className="text-[0.8rem] text-muted-foreground">注意选择复盘视频的段位</p>
            </div>
            <form.AppField
              name="videoLinks"
              mode="array"
              children={({ state, pushValue }) => (
                <div className="space-y-3">
                  {state.value.map((_, index) => (
                    <div key={`video-link-${index}`} className="flex gap-2">
                      <div className="flex-1 flex space-x-2">
                        <form.AppField
                          name={`videoLinks[${index}].url`}
                          children={(subField) => (
                            <div className="flex flex-col space-y-2 flex-1 min-w-0">
                              <Input
                                value={subField.state.value}
                                onBlur={subField.handleBlur}
                                onChange={(e) => subField.handleChange(e.target.value)}
                                id={`video-link-${index}`}
                                placeholder="视频链接如 https://www.bilibili.com/video/BVxxxxxxxxx"
                              />
                              {(subField.state.value.trim() !== "" ||
                                subField.state.meta.isTouched) && (
                                <FormErrorMessage errors={subField.state.meta.errors} />
                              )}
                            </div>
                          )}
                        />
                        {/* <form.AppField
                              name={`videoLinks[${index}].tier`}
                              children={subField => (
                                <Select
                                  value={subField.state.value}
                                  onValueChange={(value: RankedTier) => subField.handleChange(value)}
                                >
                                  <SelectTrigger className="shrink-0 w-24">
                                    <SelectValue placeholder="复盘段位" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {RankedTiers.map(tier => (
                                      <SelectItem value={tier} key={tier}>
                                        <div className="flex items-center space-x-1">
                                          <img className="size-4 translate-y-[1px]" src={RankedTierIconsMap[tier]} alt={tier} />
                                          <span>{RankedTierNameMap[tier]}</span>
                                        </div>
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              )}
                            /> */}
                      </div>
                      {state.value.length > 1 && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={() => removeVideoLink(index)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  ))}
                  {(state.value.some((link) => link.url.trim() !== "") || state.meta.isTouched) && (
                    <FormErrorMessage errors={state.meta.errors} />
                  )}
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="w-full mt-2"
                    onClick={() => pushValue({ url: "" })}
                  >
                    <Plus className="mr-2 h-4 w-4" />
                    添加视频链接
                  </Button>
                </div>
              )}
            />
          </div>

          <form.AppField
            name="description"
            children={({ Textarea, state, handleBlur, handleChange }) => (
              <div className="space-y-2 w-full">
                <label
                  className="text-sm font-semibold leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                  htmlFor="description"
                >
                  描述
                </label>
                <Textarea
                  value={state.value}
                  className="max-h-72 w-full overflow-hidden wrap-break-word"
                  onChange={(e) => handleChange(e.target.value)}
                  onBlur={handleBlur}
                  id="description"
                  rows={10}
                  placeholder="请详细描述您的复盘需求..."
                />
                {state.meta.isDirty && <FormErrorMessage errors={state.meta.errors} />}
              </div>
            )}
          />

          <form.AppField
            name="type"
            children={({ Select, state, handleChange, handleBlur }) => (
              <div className="space-y-2">
                <div className="flex justify-between">
                  <label
                    className="text-sm font-semibold leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                    htmlFor="type"
                  >
                    投稿类型
                  </label>

                  {/* <form.AppField
                    name="public"
                    children={({ Checkbox, state, handleChange, handleBlur }) => (
                      <div className="flex items-center space-x-2">
                        <label className="text-sm font-medium" htmlFor="public">是否公开</label>
                        <Checkbox
                          id="public"
                          checked={state.value}
                          onCheckedChange={value => handleChange(value as typeof state.value)}
                          onBlur={handleBlur}
                        />
                      </div>
                    )}
                  /> */}
                </div>
                <Select
                  value={state.value}
                  items={reviewTypeItems}
                  onValueChange={(value) => handleChange(value as typeof state.value)}
                  onOpenChange={(open) => {
                    if (!open) handleBlur();
                  }}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue id="type" placeholder="请选择投稿类型" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectGroup>
                      <SelectLabel>投稿类型</SelectLabel>
                      {reviewTypeItems.map((item) => (
                        <SelectItem key={item.value} value={item.value}>
                          {item.label}
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  </SelectContent>
                </Select>
                {state.meta.isDirty && <FormErrorMessage errors={state.meta.errors} />}
              </div>
            )}
          />

          {!isUpdateMode && (
            <div className="flex items-center text-sm space-x-4 min-w-0">
              <p className="text-ellipsis overflow-hidden line-clamp-1 flex-1 min-w-0">
                每次提交需要消耗 <span className="font-bold">{ApplicationCost}点</span> 积分
              </p>

              <p className="shrink-0 font-bold font-mono">
                当前积分: <span className="text-green-500">{points}</span>
              </p>

              <ClientOnly>
                <Button
                  variant="outline"
                  onClick={(e) => {
                    e.preventDefault();
                    refetchUserPoints();
                  }}
                  className="w-auto h-auto p-1 shrink-0"
                >
                  <RefreshCwIcon
                    className={clsx(isFetchingUserPoints && "animate-spin", "text-foreground")}
                  />
                </Button>
              </ClientOnly>
            </div>
          )}
        </CardContent>
        <CardFooter className="grid min-w-0 overflow-x-hidden">
          <form.AppForm>
            <form.Subscribe
              selector={(state) => [state.canSubmit, state.isSubmitting]}
              children={([canSubmit, isSubmitting]) => (
                <>
                  {isUpdateMode ? (
                    <form.Button
                      type="submit"
                      className={clsx("w-full")}
                      disabled={!canSubmit || isSubmitting || !allTermsAgreed}
                    >
                      {isSubmitting && <Loader2 className="animate-spin mr-2" />}
                      {isSubmitting ? "修改中..." : "修改申请"}
                    </form.Button>
                  ) : (
                    <Tooltip>
                      <TooltipTrigger
                        render={
                          <form.Button
                            data-umami-event="submit-review-request"
                            className={clsx(
                              "w-full",
                              insufficientPoints &&
                                "bg-destructive text-destructive-foreground hover:bg-destructive/90",
                            )}
                            type="submit"
                            disabled={!canSubmit || isSubmitting || !allTermsAgreed}
                          >
                            {isSubmitting && <Loader2 className="animate-spin mr-2" />}
                            {isSubmitting
                              ? "提交中..."
                              : insufficientPoints
                                ? "积分不足"
                                : "提交申请"}
                          </form.Button>
                        }
                      />
                      <TooltipContent>
                        <p className="text-sm text-orange-500">
                          {!allTermsAgreed
                            ? "请先勾选以上全部说明后再提交"
                            : "你可以通过邀请奖励获取积分，或是等待之前的复盘申请完成后返还积分"}
                        </p>
                      </TooltipContent>
                    </Tooltip>
                  )}
                </>
              )}
            />
          </form.AppForm>
        </CardFooter>
      </Card>

      <ContactUs />
    </form>
  );
}
